import {inject, Injectable} from "@angular/core"
import {fetchOk} from "@cm/utils"
import {FilesService} from "@app/common/services/files/files.service"
import type {PdfDownloadOptions} from "@app/common/components/viewers/configurator/types/cm-configurator"
import {currencyToSymbol} from "@cm/pricing/types/currency"
import {PdfSettings} from "@cm/template-nodes"
import {PdfDataFragment} from "@common/components/viewers/configurator/configurator/configurator.generated"
import {PricingService} from "@pricing/services/pricing.service"
import type {PDFDocument, PDFFont, PDFPage, RGB} from "pdf-lib"

function hexToRgb(hex: string): RGB {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    if (!result) throw new Error(`Invalid hex color: ${hex}`)
    return {type: "RGB", red: parseInt(result[1], 16) / 255, green: parseInt(result[2], 16) / 255, blue: parseInt(result[3], 16) / 255} as RGB
}

export enum TextMode {
    Ellipsis,
    Wrap,
}

@Injectable({
    providedIn: "root",
})
export class PdfGenerationService {
    private readonly pricingService = inject(PricingService)

    async generatedAndDownloadPdf(jpgImageBytes: string, pdfData: PdfDataFragment, pdfSettings: PdfSettings, options?: PdfDownloadOptions) {
        const pdf = await this.generatePdf(jpgImageBytes, pdfData, pdfSettings, options)
        FilesService.downloadFile("Configuration.pdf", pdf)
    }

    async generatePdf(jpgImageBytes: string, pdfTemplateData: PdfDataFragment, pdfSettings: PdfSettings, options?: PdfDownloadOptions) {
        const pdfUrl = pdfTemplateData.pdfTemplateUrl
        if (!pdfUrl) throw new Error("No PDF template found.")

        const fontUrl = pdfTemplateData.fontUrl
        if (!fontUrl) throw new Error("No font found.")

        const fontBoldUrl = pdfTemplateData.fontBoldUrl
        if (!fontBoldUrl) throw new Error("No bold font found.")

        const fontBytes = await fetchOk(fontUrl).then((res) => res.arrayBuffer())
        const fontBoldBytes = await fetchOk(fontBoldUrl).then((res) => res.arrayBuffer())

        const existingPdfBytes = await fetchOk(pdfUrl).then((res) => res.arrayBuffer())

        const {PDFDocument} = await import("pdf-lib")
        const pdfDoc = await PDFDocument.load(existingPdfBytes)
        const pages = pdfDoc.getPages()
        if (pages.length != 1) throw Error(`The PDF template has to have exactly 1 page. Pages found: ${pages.length}.`)
        const page = pages[0]

        const {default: fontkit} = await import("@pdf-lib/fontkit")
        pdfDoc.registerFontkit(fontkit)

        const pageStyle = new PageStyle(page, await pdfDoc.embedFont(fontBytes), await pdfDoc.embedFont(fontBoldBytes), pdfTemplateData.styleSettings)

        const title = options?.title ?? pdfSettings.parameters.title ?? "Your Configuration"
        pdfDoc.setTitle(title)
        const headlineBottomY = await this.addHeadline(page, pageStyle, title)

        const screenshotY = headlineBottomY - pageStyle.headlinePadding
        const screenshotBottomY = await PdfGenerationService.addScreenshot(pdfDoc, jpgImageBytes, pageStyle, page, screenshotY)

        const tableStartY = screenshotBottomY - pageStyle.screenshotPadding
        if (options?.table) {
            await this.addExternalTable(page, pageStyle, tableStartY, options.table)
        } else {
            await this.addPricingTable(page, pageStyle, tableStartY)
        }

        return pdfDoc.saveAsBase64({dataUri: true})
    }

    async addPricingTable(page: PDFPage, pageStyle: PageStyle, startY: number) {
        const headers = ["DESCRIPTION", "SKU", "AMOUNT", "PRICE"]
        const columnsPercent = [50, 25, 15, 10]

        const {items} = await this.pricingService.getPrices()
        if (items.length === 0) return

        const rows = items.map((price) => [
            price.description,
            price.sku ? price.sku : "N/A",
            price.amount.toString(),
            `${currencyToSymbol(price.currency)} ${price.price}`,
        ])

        const nextLineY = await this.addTable(page, pageStyle, startY, headers, rows, columnsPercent, TextMode.Wrap)

        const tableWidth = pageStyle.pageWidth - 2 * pageStyle.pageMarginLeftRight

        const totalStart = pageStyle.pageMarginLeftRight + (tableWidth * columnsPercent.slice(0, 2).reduce((acc, val) => acc + val, 0)) / 100
        page.drawText("TOTAL:", {x: totalStart, y: nextLineY, font: pageStyle.fontBold, size: pageStyle.fontSize, color: pageStyle.fontColorBold})

        const sum = PricingService.computeTotalPrice(items)
        const sumStart = pageStyle.pageMarginLeftRight + (tableWidth * columnsPercent.slice(0, 3).reduce((acc, val) => acc + val, 0)) / 100
        page.drawText(`${currencyToSymbol(items[0].currency)} ${sum}`, {
            x: sumStart,
            y: nextLineY,
            font: pageStyle.fontBold,
            size: pageStyle.fontSize,
            color: pageStyle.fontColorBold,
        })
    }

    private async addExternalTable(page: PDFPage, pageStyle: PageStyle, startY: number, table: NonNullable<PdfDownloadOptions["table"]>) {
        if (table.rows.length === 0) return

        const columnCount = table.headers.length
        const columnsPercent = table.columnWidthPercentages ?? Array<number>(columnCount).fill(100 / columnCount)

        const nextLineY = await this.addTable(page, pageStyle, startY, table.headers, table.rows, columnsPercent, TextMode.Wrap)

        if (table.total) {
            const tableWidth = pageStyle.pageWidth - 2 * pageStyle.pageMarginLeftRight

            const totalLabelX =
                pageStyle.pageMarginLeftRight + (tableWidth * columnsPercent.slice(0, columnsPercent.length - 2).reduce((acc, val) => acc + val, 0)) / 100
            page.drawText(table.total.label, {x: totalLabelX, y: nextLineY, font: pageStyle.fontBold, size: pageStyle.fontSize, color: pageStyle.fontColorBold})

            const totalValueX =
                pageStyle.pageMarginLeftRight + (tableWidth * columnsPercent.slice(0, columnsPercent.length - 1).reduce((acc, val) => acc + val, 0)) / 100
            page.drawText(table.total.value, {x: totalValueX, y: nextLineY, font: pageStyle.fontBold, size: pageStyle.fontSize, color: pageStyle.fontColorBold})
        }
    }

    async addTable(
        page: PDFPage,
        pageStyle: PageStyle,
        startY: number,
        headers: string[],
        rows: string[][],
        columnWidthPercentages: number[],
        textMode: TextMode,
    ): Promise<number> {
        const totalAvailableWidth = pageStyle.pageWidth - 2 * pageStyle.pageMarginLeftRight
        const columnWidths = columnWidthPercentages.map((percent) => (totalAvailableWidth * percent) / 100)
        const startX = pageStyle.pageMarginLeftRight + (totalAvailableWidth - columnWidths.reduce((acc, width) => acc + width, 0)) / 2

        const lineHeight = pageStyle.fontSize * pageStyle.tableLineHeight
        const avgCharWidth = pageStyle.fontSize * 0.5
        const rowPadding = textMode === TextMode.Wrap ? 8 : 0

        const drawTextWithEllipsis = (text: string, x: number, y: number, maxWidth: number): number => {
            let trimmedText = text
            const maxChars = Math.floor(maxWidth / avgCharWidth)
            if (text.length > maxChars) trimmedText = text.substring(0, maxChars - 1) + "..."
            page.drawText(trimmedText, {x, y, font: pageStyle.font, size: pageStyle.fontSize})
            return 1
        }

        const drawTextWrapped = (text: string, x: number, y: number, maxWidth: number): number => {
            const words = text.split(" ")
            let line = ""
            let linesDrawn = 0

            for (const word of words) {
                const testLine = line + word + " "
                const testWidth = testLine.length * avgCharWidth
                if (testWidth > maxWidth && line !== "") {
                    page.drawText(line, {x, y: y - linesDrawn * lineHeight, font: pageStyle.font, size: pageStyle.fontSize, color: pageStyle.fontColor})
                    line = word + " "
                    linesDrawn++
                } else {
                    line = testLine
                }
            }
            page.drawText(line, {x, y: y - linesDrawn * lineHeight, font: pageStyle.font, size: pageStyle.fontSize, color: pageStyle.fontColor})
            return linesDrawn + 1
        }

        const drawTextFunction = textMode === TextMode.Ellipsis ? drawTextWithEllipsis : drawTextWrapped

        headers.forEach((header, index) => {
            const headerX = startX + columnWidths.slice(0, index).reduce((acc, val) => acc + val, 0)
            page.drawText(header, {x: headerX, y: startY, font: pageStyle.fontBold, size: pageStyle.headerFontSize, color: pageStyle.fontColorBold})
        })

        let currentY = startY - lineHeight - rowPadding

        rows.forEach((row) => {
            let maxLinesInRow = 1
            row.forEach((cell, cellIndex) => {
                const cellX = startX + columnWidths.slice(0, cellIndex).reduce((acc, val) => acc + val, 0)
                const linesDrawn = drawTextFunction(cell, cellX, currentY, columnWidths[cellIndex])
                maxLinesInRow = Math.max(maxLinesInRow, linesDrawn)
            })
            currentY -= maxLinesInRow * lineHeight + rowPadding
        })

        return currentY
    }

    private static async addScreenshot(pdfDoc: PDFDocument, jpgImageBytes: string, pageStyle: PageStyle, page: PDFPage, imageY: number) {
        const snapshotImage = await pdfDoc.embedJpg(jpgImageBytes)
        const snapshotSize = snapshotImage.scaleToFit(pageStyle.pageWidth - 2 * pageStyle.pageMarginLeftRight, pageStyle.screenshotMaxHeight)
        let imageX = pageStyle.pageMarginLeftRight
        if (snapshotSize.width < pageStyle.pageWidth - 2 * pageStyle.pageMarginLeftRight) {
            imageX = pageStyle.pageMarginLeftRight + (pageStyle.pageWidth - 2 * pageStyle.pageMarginLeftRight - snapshotSize.width) / 2
        }

        const y = imageY - snapshotSize.height
        page.drawImage(snapshotImage, {
            x: imageX,
            y: y,
            width: snapshotSize.width,
            height: snapshotSize.height,
        })

        return y
    }

    /*Character spacing is not supported by pdf-lib but was desired by vado.*/
    private drawTextWithManualSpacing(page: PDFPage, font: PDFFont, fontSize: number, color: RGB, x: number, y: number, text: string, charSpacing: number) {
        let currentX = x
        for (let i = 0; i < text.length; i++) {
            page.drawText(text[i], {
                x: currentX,
                y,
                font: font,
                size: fontSize,
                color: color,
            })
            currentX += font.widthOfTextAtSize(text[i], fontSize)
            if (i < text.length - 1) currentX += charSpacing
        }
    }

    /**
     * Draws a headline at the top of the page, using font size and color from PageStyle.
     * Returns the y-position after the headline (bottom of headline minus padding).
     */
    async addHeadline(page: PDFPage, pageStyle: PageStyle, text: string): Promise<number> {
        const fontSize = pageStyle.headlineFontSize
        const font = pageStyle.headlineFont
        const color = pageStyle.headlineFontColor
        const charSpacing = pageStyle.headlineCharSpacing
        const availableWidth = pageStyle.pageWidth - 2 * pageStyle.pageMarginLeftRight
        let textWidth = font.widthOfTextAtSize(text, fontSize)

        let usedFontSize = fontSize
        while (textWidth > availableWidth) {
            usedFontSize -= 1
            textWidth = font.widthOfTextAtSize(text, usedFontSize)
        }

        let manualTextWidth = 0
        for (let i = 0; i < text.length; i++) {
            manualTextWidth += font.widthOfTextAtSize(text[i], usedFontSize)
            if (i < text.length - 1) manualTextWidth += charSpacing
        }
        const x = pageStyle.pageMarginLeftRight + (availableWidth - manualTextWidth) / 2
        const y = pageStyle.pageHeight - pageStyle.pageMarginTop - usedFontSize

        this.drawTextWithManualSpacing(page, font, usedFontSize, color, x, y, text, charSpacing)

        return y - pageStyle.headlinePadding
    }
}

/*CS in pdf-lib: Origin bottom left, x right, y up. Standard size of an a4 page is 595 x 842
Numbers below assume image conventions, i.e. origin top left, x right, y down*/
class PageStyle {
    pageMarginTop: number
    pageMarginBottom: number
    pageMarginLeftRight = 50 //72 = 1 in

    headlineFontSize: number
    headlineCharSpacing: number
    headlineFont: PDFFont
    headlineFontColor: RGB
    headlinePadding: number

    screenshotPadding: number
    screenshotMaxHeight: number

    pageWidth: number
    pageHeight: number

    headerFontSize: number
    fontSize: number
    tableLineHeight: number
    font: PDFFont
    fontBold: PDFFont

    fontColor: RGB
    fontColorBold: RGB

    constructor(page: PDFPage, font: PDFFont, fontBold: PDFFont, styleSettings?: PdfDataFragment["styleSettings"]) {
        this.pageWidth = page.getWidth()
        this.pageHeight = page.getHeight()
        this.screenshotMaxHeight = 0.35 * this.pageHeight
        this.font = font
        this.fontBold = fontBold
        this.pageMarginTop = styleSettings?.marginTop ?? 40
        this.pageMarginBottom = styleSettings?.marginBottom ?? 0
        this.headerFontSize = styleSettings?.tableHeaderFontSize ?? styleSettings?.tableFontSize ?? 9
        this.fontSize = styleSettings?.tableFontSize ?? 9
        this.tableLineHeight = styleSettings?.tableLineHeight ?? 1.8
        this.headlineFontSize = styleSettings?.headlineFontSize ?? 40
        this.headlineCharSpacing = styleSettings?.headlineCharSpacing ?? 4
        this.headlineFont = styleSettings?.headlineBold ? fontBold : font
        this.headlinePadding = styleSettings?.headlinePadding ?? 20
        this.screenshotPadding = styleSettings?.imagePadding ?? 30
        this.headlineFontColor = styleSettings?.accentColor
            ? hexToRgb(styleSettings.accentColor)
            : ({type: "RGB", red: 0, green: 140 / 255, blue: 153 / 255} as RGB)
        this.fontColor = styleSettings?.textColor ? hexToRgb(styleSettings.textColor) : ({type: "RGB", red: 65 / 255, green: 64 / 255, blue: 66 / 255} as RGB)
        this.fontColorBold = styleSettings?.accentColor
            ? hexToRgb(styleSettings.accentColor)
            : ({type: "RGB", red: 0, green: 140 / 255, blue: 153 / 255} as RGB)
    }
}
